import { useState, useEffect } from 'react';
import { Card, CardMedia, Typography, Box, Paper, CircularProgress } from '@mui/material';
import axios from 'axios';

function Announcement() {
  const [announcements, setAnnouncements] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceType, setDeviceType] = useState('Desktop');
  const [browserType, setBrowserType] = useState('Unknown');

  // Function to get the full image URL from Strapi
  const getImageUrl = (imageData) => {
    if (!imageData) {
      return '/images/announcements/default.png';
    }

    // If imageData is a string (direct URL), return it
    if (typeof imageData === 'string') {
      return imageData;
    }

    // If imageData is an object with url property (Strapi format)
    if (imageData.url) {
      // Check if it's already a full URL
      if (imageData.url.startsWith('http')) {
        return imageData.url;
      }
      // Otherwise prepend the Strapi base URL
      return `${process.env.REACT_APP_API_URL}${imageData.url}`;
    }

    return '/images/announcements/default.png';
  };

  // Device detection
    const detectDevice = () => {
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      let detectedDeviceType = 'Desktop';
      let os = 'Unknown';
      let detectedBrowser = 'Unknown';

      // Detect device type
      if (/Android/i.test(userAgent)) {
        detectedDeviceType = 'Mobile - Android';
        os = 'Android';
      } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
        detectedDeviceType = /iPad/i.test(userAgent) ? 'Tablet - iPad' : 'Mobile - iPhone';
        os = 'iOS';
      } else if (/Windows/i.test(userAgent) || /Win/i.test(platform)) {
        os = 'Windows';
      } else if (/Mac/i.test(userAgent) || /Mac/i.test(platform)) {
        os = 'macOS';
      } else if (/Linux/i.test(userAgent)) {
        os = 'Linux';
      }

      // Detect browser
      if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) {
        detectedBrowser = 'Chrome';
      } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
        detectedBrowser = 'Safari';
      } else if (/Firefox/i.test(userAgent)) {
        detectedBrowser = 'Firefox';
      } else if (/Edg/i.test(userAgent)) {
        detectedBrowser = 'Edge';
      }

      // Update state variables
      setDeviceType(detectedDeviceType);
      setBrowserType(detectedBrowser);

      console.log('=== Device Information ===');
      console.log('Device Type:', detectedDeviceType);
      console.log('Operating System:', os);
      console.log('Browser:', detectedBrowser);
      console.log('Screen Size:', `${screenWidth}x${screenHeight}`);
      console.log('User Agent:', userAgent);
      console.log('========================');
    };

  // Handle image click - redirect based on device type
  const handleImageClick = () => {
    if (deviceType.includes('iPhone') || deviceType.includes('iPad')) {
      // Redirect iOS devices to Apple App Store
      window.location.href = 'https://apps.apple.com';
    } else if (deviceType.includes('Android')) {
      // Redirect Android devices to Google Play Store
      window.location.href = 'https://play.google.com';
    }
  };

  useEffect(() => {

    detectDevice();

    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL;
        const apiToken = process.env.REACT_APP_API_TOKEN;

        const response = await axios.get(`${apiUrl}/api/news-annoucements?populate=*&pagination[page]=1&pagination[pageSize]=5&sort=sequence:asc`, {
          headers: {
            'Authorization': `Bearer ${apiToken}`,
            'Content-Type': 'application/json'
          }
        });

        console.log(response.data.data);

        // Transform API data to match our component's expected format
        const transformedData = response.data.data.map(item => ({
          id: item.id,
          documentId: item.documentId,
          title: item.title || 'Untitled',
          imageinfo: item.image,
          image: getImageUrl(item.image),
          description: item.description || '',
        }));

        setAnnouncements(transformedData);
        if (transformedData.length > 0) {
          setSelectedItem(transformedData[0]);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching announcements:', err);
        setError(err.response?.data?.message || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="error">Error loading announcements: {error}</Typography>
      </Box>
    );
  }

  if (announcements.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>No announcements available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      flexGrow: 1,
      height: '100%',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden'
    }}>
      <Box sx={{
        width: { xs: '140px', sm: '200px', md: '260px' },
        minWidth: { xs: '140px', sm: '200px', md: '260px' },
        maxWidth: { xs: '140px', sm: '200px', md: '260px' },
        paddingTop: { xs: 1, sm: 1.5, md: 2 },
        paddingBottom: { xs: 0.5, sm: 1, md: 1.5 },
        paddingLeft: { xs: 0.5, sm: 1, md: 1.5 },
        paddingRight: { xs: 1.5, sm: 2, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflowY: 'auto',
        overflowX: 'visible',
        flexShrink: 0,
        backgroundColor: 'transparent'
      }}>
        {announcements.map((item) => (
          <Box key={item.id} sx={{ position: 'relative', overflow: 'visible' }}>
            <Card
              onClick={() => setSelectedItem(item)}
              sx={{
                cursor: 'pointer',
                border: selectedItem.id === item.id ? '6px solid #ff5722' : 'none',
                borderRadius: '12px',
                overflow: 'hidden',
                '&:hover': {
                  boxShadow: 4
                }
              }}
            >
              <CardMedia
                component="img"
                sx={{
                  width: '100%',
                  aspectRatio: '3/2',
                  objectFit: 'cover'
                }}
                image={item.image}
                alt={item.title}
              />
              {/* <CardContent>
                <Typography variant="body2" color="text.secondary">
                  {item.title}
                </Typography>
              </CardContent> */}
            </Card>
            {selectedItem.id === item.id && (
              <Box
                sx={{
                  position: 'absolute',
                  right: { xs: '-10px', sm: '-10px', md: '-12px' },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: { xs: '10px solid transparent', sm: '10px solid transparent', md: '12px solid transparent' },
                  borderBottom: { xs: '10px solid transparent', sm: '10px solid transparent', md: '12px solid transparent' },
                  borderLeft: { xs: '12px solid #ff5722', sm: '12px solid #ff5722', md: '14px solid #ff5722' },
                  zIndex: 1
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1, paddingTop: { xs: 1, md: 2 }, paddingBottom: { xs: 1, md: 2 }, paddingLeft: 0, paddingRight: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column' }}>        <Paper elevation={3} sx={{
          padding: { xs: 2, sm: 2.5, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '5px', marginBottom: { xs: '10px', md: '20px' } }}>
            <img
              src={selectedItem.image}
              alt={selectedItem.title}
              onClick={handleImageClick}
              style={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '6px',
                cursor: deviceType.includes('iPhone') || deviceType.includes('iPad') || deviceType.includes('Android') ? 'pointer' : 'default'
              }}
            />
          </Box>
          <Typography
            variant="body1"
            paragraph
            dangerouslySetInnerHTML={{ __html: selectedItem.description }}
          />
        </Paper>
      </Box>
    </Box>
  );
}

export default Announcement;
